import { LogOut, RefreshCw, WifiOff } from "lucide-react";
import type { ReactElement } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { AuthPageShell } from "@/components/layout/AuthPageShell";
import { AuthPanel, AuthPanelHeader } from "@/components/layout/AuthPanel";
import { Button } from "@/components/ui/button";
import { CardFooter } from "@/components/ui/card";
import { signOutClient } from "@/lib/signOut";

type AuthLoadErrorProps = {
  onRetry: () => void;
};

export function AuthLoadError({ onRetry }: AuthLoadErrorProps): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isSigningOut, setIsSigningOut] = useState(false);

  async function handleSignOut(): Promise<void> {
    setIsSigningOut(true);
    try {
      await signOutClient();
      navigate("/login", { replace: true });
    } finally {
      setIsSigningOut(false);
    }
  }

  return (
    <AuthPageShell>
      <AuthPanel>
        <AuthPanelHeader
          icon={<WifiOff className="size-6 text-primary" strokeWidth={1.75} />}
          title={t("auth.profileLoadErrorTitle")}
          description={t("auth.profileLoadError")}
        />
        <CardFooter className="w-full flex-row justify-between gap-3">
          <Button
            type="button"
            variant="outline"
            size="lg"
            className="gap-2 px-5"
            disabled={isSigningOut}
            onClick={() => void handleSignOut()}
          >
            <LogOut className="size-4 shrink-0" aria-hidden />
            {isSigningOut ? t("common.loading") : t("auth.signOut")}
          </Button>
          <Button type="button" size="lg" className="gap-2 px-5" onClick={onRetry}>
            <RefreshCw className="size-4 shrink-0" aria-hidden />
            {t("auth.retry")}
          </Button>
        </CardFooter>
      </AuthPanel>
    </AuthPageShell>
  );
}
